import { ClipboardList, Radio, Settings2 } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/Feedback';
import {
  formatAbsolute,
  formatBytes,
  formatDuration,
  formatRelative,
  humaniseAction,
} from '@/lib/format';
import type {
  AuditEvent,
  AuditOutcome,
  AuditSeverity,
  RemoteSession,
  SessionStatus,
} from '@/lib/api/types';

/*
  The Activity tab: three lists, in the order a technician reads them.

  1. Recent sessions. What happened at the machine, who did it, how long for, and whether the
     customer consented.
  2. Technician activity. Connects, reboots, file transfers, including the denied attempts.
  3. Administrative actions. Changes to the record itself: enrollment, moves, tag edits,
     revocation.

  Two and three are separated because they answer different questions and are usually read by
  different people. The split is made server side, by which permission authorized the action;
  the reasoning is written down in mockGetDevice in src/mocks/handlers.ts.

  Denials are shown, not filtered out. docs/07-identity-rbac.md I.3 records denials on purpose,
  and a denied file transfer against this machine is exactly the row an investigation is
  looking for. Hiding it here would defeat the point of writing it.
*/

const SEVERITY_TONE: Readonly<Record<AuditSeverity, BadgeTone>> = {
  info: 'neutral',
  notice: 'info',
  warning: 'warning',
  critical: 'danger',
};

const OUTCOME_TONE: Readonly<Record<AuditOutcome, BadgeTone>> = {
  success: 'success',
  denied: 'danger',
  failure: 'warning',
};

const SESSION_TONE: Readonly<Record<SessionStatus, BadgeTone>> = {
  pending: 'info',
  waiting_consent: 'warning',
  active: 'success',
  ended: 'neutral',
  denied: 'danger',
  expired: 'neutral',
  failed: 'danger',
};

function AuditRows({ events }: { readonly events: readonly AuditEvent[] }): React.JSX.Element {
  return (
    <ul>
      {events.map((event) => (
        <li key={event.id} className="border-b border-border px-4 py-3 last:border-b-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{humaniseAction(event.action)}</span>
            <Badge tone={OUTCOME_TONE[event.outcome]}>{event.outcome}</Badge>
            <Badge tone={SEVERITY_TONE[event.severity]}>{event.severity}</Badge>
            {event.permission === null ? null : (
              <Badge tone="neutral" title="The permission the authorization layer evaluated">
                <span className="font-mono text-[11px]">{event.permission}</span>
              </Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            {event.actorName ?? 'Unknown actor'}
            {event.actorIp === null ? '' : ` from ${event.actorIp}`}
            {', '}
            <time dateTime={event.occurredAt} title={formatAbsolute(event.occurredAt)}>
              {formatRelative(event.occurredAt)}
            </time>
          </p>
          {event.detail === null ? null : (
            <p className="mt-1 text-xs leading-relaxed text-fg-subtle">{event.detail}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

export interface DeviceActivityPanelProps {
  readonly hostname: string;
  readonly sessions: readonly RemoteSession[];
  readonly technicianActivity: readonly AuditEvent[];
  readonly administrativeActivity: readonly AuditEvent[];
}

export function DeviceActivityPanel({
  hostname,
  sessions,
  technicianActivity,
  administrativeActivity,
}: DeviceActivityPanelProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Radio className="size-4 text-fg-subtle" aria-hidden="true" />
              Recent sessions
            </span>
          }
          description={`Remote sessions against ${hostname}, newest first.`}
          headingLevel={3}
        />
        {sessions.length === 0 ? (
          <EmptyState
            icon={<Radio className="size-5" aria-hidden="true" />}
            title="No sessions on this device yet"
            description="Nobody has connected to it through JIT. Sessions appear here as soon as one is started, including ones the customer declined."
          />
        ) : (
          <ul>
            {sessions.map((session) => (
              <li key={session.id} className="border-b border-border px-4 py-3 last:border-b-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={SESSION_TONE[session.status]}>
                    {session.status.replace('_', ' ')}
                  </Badge>
                  <span className="text-sm text-fg">
                    {session.createdByName ?? 'Unknown technician'}
                  </span>
                  <span className="text-xs text-fg-subtle">
                    {session.mode === 'quick_support' ? 'Quick Support' : 'Unattended'}
                  </span>
                  {session.connectionType === null ? null : (
                    <Badge
                      tone="neutral"
                      title={
                        session.connectionType === 'relay'
                          ? 'Went through a TURN relay, which forwards ciphertext it cannot read.'
                          : 'Peer to peer, no relay involved.'
                      }
                    >
                      {session.connectionType}
                    </Badge>
                  )}
                </div>

                <p className="mt-1 text-xs text-fg-muted">
                  <time dateTime={session.createdAt} title={formatAbsolute(session.createdAt)}>
                    {formatRelative(session.createdAt)}
                  </time>
                  {session.startedAt === null
                    ? ', never connected'
                    : `, ran ${formatDuration(session.startedAt, session.endedAt)}`}
                  {session.endReason === null ? '' : `, ended: ${session.endReason.replace(/_/g, ' ')}`}
                </p>

                <p className="mt-1 text-xs text-fg-subtle">
                  Requested {session.requestedCaps.join(', ')}
                  {'; granted '}
                  {session.grantedCaps.length === 0 ? 'nothing' : session.grantedCaps.join(', ')}
                  {session.consentRequired
                    ? session.consentGrantedAt === null
                      ? '. Consent was required and not given.'
                      : '. Customer consented.'
                    : '. Consent not required by policy.'}
                  {session.bytesSent + session.bytesReceived > 0
                    ? ` Transferred ${formatBytes(session.bytesSent + session.bytesReceived)}.`
                    : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <ClipboardList className="size-4 text-fg-subtle" aria-hidden="true" />
              Technician activity
            </span>
          }
          description="Connects, reboots and file transfers against this machine, including attempts that were refused."
          headingLevel={3}
        />
        {technicianActivity.length === 0 ? (
          <p className="px-4 py-6 text-sm text-fg-subtle italic">
            No technician has acted on this device yet.
          </p>
        ) : (
          <AuditRows events={technicianActivity} />
        )}
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Settings2 className="size-4 text-fg-subtle" aria-hidden="true" />
              Administrative actions
            </span>
          }
          description="Changes to the device record: enrollment, moves, tag edits, policy and revocation."
          headingLevel={3}
        />
        {administrativeActivity.length === 0 ? (
          <p className="px-4 py-6 text-sm text-fg-subtle italic">
            Nobody has changed this record since it was enrolled.
          </p>
        ) : (
          <AuditRows events={administrativeActivity} />
        )}
      </Card>
    </div>
  );
}
